import {FC, useState} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useBulkUnenrollUsersFromPlaylist} from "@/app2/shared/data";
import {palette} from "@/constants";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	playlistId: number;
}

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export const BulkPlaylistUnenrollmentConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, playlistId}) => {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	const bulkUnenrollMutation = useBulkUnenrollUsersFromPlaylist(playlistId);
	const [isLoading, setIsLoading] = useState(false);

	const handleConfirm = () => {
		if (!open || isLoading) {
			return;
		}

		setIsLoading(true);

		// Use HTTP mutation instead of WebSocket
		bulkUnenrollMutation.mutate(
			{userIds: selectedUserIds},
			{
				onSuccess: () => {
					successSnackbar(t("bulk_playlist_unenrollment_success", `Successfully unenrolled ${selectedUserIds.length} user(s) from playlist`));
					setIsLoading(false);
					onClose();
				},
				onError: (error) => {
					errorSnackbar(t("bulk_playlist_unenrollment_error", `Failed to unenroll users: ${error.message}`));
					setIsLoading(false);
				}
			}
		);
	};

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick" && !isLoading) {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_playlist_unenrollment_modal_title", "Unenroll Users from Playlist", {count: selectedUserIds.length})}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
					{t("bulk_playlist_unenrollment_modal_body_copy", "This will unenroll the selected users from all courses in this playlist. This action cannot be undone.", {count: selectedUserIds.length})}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={isLoading}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={handleConfirm}
					sx={buttonSx}
					disabled={isLoading}
					startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
				>
					{isLoading ? t("common_unenrolling", "Unenrolling...") : t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
